import { Controller } from "@hotwired/stimulus"
import { replacePlan, deepMerge } from "plan_storage"
import { encryptPlan, decryptPlan } from "sdp_crypto"

// .sdp open/save controller — wires the toolbar's Open + Save buttons
// to client-side AES-GCM-256 + PBKDF2-SHA256-600k encryption via the
// Web Crypto API. Format matches pcp_builder.html's CryptoUtils
// verbatim — the on-disk byte layout is
//
//     base64( salt[16] | iv[12] | ciphertext )
//
// so .sdp files written by the iframe editor open in the native editor
// and vice versa. Per EDIT-002A / EDIT-003A / EDIT-004B everything
// stays in the browser; the file picker / download flow never touches
// the Rails server.

const STORAGE_KEY = "pcp_plan_data"

// Legacy editor's plan skeleton (pcp_builder.html INITIAL_STATE). The
// legacy loader accesses `decrypted.meta.pageBreaks` and
// `decrypted.about.communicationChart` WITHOUT guarding that meta/about
// exist, so a native plan missing those objects makes the legacy editor
// throw and report "Incorrect password or invalid file". We merge the
// plan over this skeleton on export so every .sdp we write has the full
// top-level shape the legacy tool expects (empty defaults never clobber
// real data — the user's values always win via deepMerge).
const LEGACY_SKELETON = {
  meta: { pageBreaks: {}, summaryChecks: {}, proofread: {} },
  client: {},
  facilitator: {},
  about: { whoLivesWithMe: [], communicationStyle: [], schoolDetails: [], workDetails: [], communicationChart: [], circleOfSupport: [], primaryLanguages: "", culturalIdentity: "", livingSituation: "" },
  important: { to: [], for: [] },
  working: { works: [], doesntWork: [] },
  supportMatch: { qualities: [], skills: [], personality: [], dealBreakers: [] },
  wonderfulThings: { activities: [], places: [], events: [], people: [], foods: [], possessions: [], admiredQualities: [] },
  notSoFavorites: { triggers: [], sensory: [], situations: [], foods: [], environments: [] },
  bigDreams: { hopes: [], dreams: [] },
  dayInLife: { morning: [], day: [], afternoon: [], night: [], afterBed: [], weekendMorning: [], weekendDay: [], weekendAfternoon: [], weekendNight: [], weekendAfterBed: [] },
  resourceCalendar: { enabled: false, slots: [] },
  resources: { unmetNeeds: [], genericResources: [], naturalSupports: [] },
  currentServices: [],
  barriers: [],
  unmetNeeds: [],
  goals: [],
  review: {}
}

// encryptPlan / decryptPlan (AES-GCM-256 + PBKDF2) now live in sdp_crypto.js so
// the byte format is unit-testable; imported at the top of this file.

function suggestedFilename(plan) {
  const c = plan.client || {}
  // Prefer the First/Last Name fields — those are what the native editor
  // actually edits (there is no combined "name" input). Fall back to the
  // legacy combined `client.name` only when both are blank (older plans /
  // imported .sdp), so the file is named FirstName_LastName.sdp like the
  // legacy editor rather than from a stale `name`.
  const joined = `${c.firstName || ""} ${c.lastName || ""}`.trim()
  const name = joined || (c.name || "").trim()
  const safe = name.replace(/[^a-z0-9_-]+/gi, "_").replace(/^_+|_+$/g, "") || "Plan"
  return `${safe}.sdp`
}

// "Remember on this device" cache. Persisted in localStorage so it survives
// page refreshes, deploys (asset-track reload), closing the tab, AND a browser
// restart — the user isn't re-prompted until they clear it (uncheck Remember
// on a save) or clear site data. Base64 keeps it from sitting as literal
// plaintext in devtools; it is NOT encryption (an XSS could still read it —
// same exposure as the plan, which already lives in localStorage).
const SDP_PW_KEY = "pcpm_sdp_pw"

export default class extends Controller {
  static targets = ["openBtn", "saveBtn", "fileInput"]

  connect() {
    this._restorePassword()
    this._maybeSeedFromDesktop()
  }

  // PCPDesk desktop host: when a client's working copy is still empty, seed the
  // editor from that client's LATEST saved .sdp (their real PCP) so opening a
  // client shows their existing plan instead of a blank one. The host exposes
  // window.__PCP_GET_LATEST_SDP__() → the encrypted .sdp text (or null). We
  // decrypt with the remembered password, prompting once if there isn't one.
  async _maybeSeedFromDesktop() {
    if (!window.__PCP_DESKTOP__ || typeof window.__PCP_GET_LATEST_SDP__ !== "function") return
    const cur = this._load()
    const hasContent = cur && Object.keys(cur).some(k =>
      k !== "meta" && cur[k] && (typeof cur[k] !== "object" || Object.keys(cur[k]).length))
    if (hasContent) return // already has a working plan — don't overwrite it

    let sdp = null
    try { sdp = window.__PCP_GET_LATEST_SDP__() } catch (_) { sdp = null }
    if (!sdp) return // client has no saved .sdp yet → start blank

    let plan = null
    if (this._lastPassword) {
      try { plan = await decryptPlan(String(sdp).trim(), this._lastPassword) } catch (_) { plan = null }
    }
    if (!plan) {
      const res = await this._promptPassword({
        title:  "Open latest PCP",
        hint:   "Enter the password for this client’s saved PCP (.sdp).",
        submit: "Open",
        remember: true,
      })
      if (!res || !res.password) return // cancelled → leave blank; they can Open manually
      try { plan = await decryptPlan(String(sdp).trim(), res.password) }
      catch (_) { this._notify("Wrong password for the saved PCP.", "err"); return }
      this._rememberPassword(res.remember ? res.password : null)
    }

    replacePlan(plan)
    // Reload so every section re-hydrates from the seeded plan.
    setTimeout(() => location.reload(), 200)
  }

  // Cache the password on this device when set; clear it when passed null
  // (user unticked "Remember"). localStorage → survives refresh / deploy /
  // tab-close / browser restart.
  _rememberPassword(pw) {
    this._lastPassword = pw || null
    try {
      if (pw) localStorage.setItem(SDP_PW_KEY, btoa(unescape(encodeURIComponent(pw))))
      else    localStorage.removeItem(SDP_PW_KEY)
    } catch (_) { /* private mode / quota — cache stays in-memory only */ }
  }

  _restorePassword() {
    try {
      const v = localStorage.getItem(SDP_PW_KEY)
      this._lastPassword = v ? decodeURIComponent(escape(atob(v))) : null
    } catch (_) { this._lastPassword = null }
  }

  async save(event) {
    event?.preventDefault()
    const plan = this._load()
    if (!plan || !plan.client) {
      this._notify("Add some client info before saving.", "warn")
      return
    }
    // Reuse the password remembered on this device so the user isn't
    // re-prompted on every re-save (or after a deploy / browser restart).
    // Shift+click forces a re-prompt when they want to rotate the password;
    // unticking "Remember" on a save clears the stored copy.
    let password = (!event?.shiftKey && this._lastPassword) || null
    if (!password) {
      const res = await this._promptPassword({
        title:   "Encrypt & Save",
        hint:    "Set a password to protect this .sdp file (AES-GCM-256). There's no recovery if you forget it.",
        submit:  "Download",
        confirm: true,
        remember: true
      })
      if (!res || !res.password) return
      password = res.password
      // Only keep the password for the rest of the session if they ticked
      // "Remember for this session" (persisted across refreshes below).
      this._rememberPassword(res.remember ? password : null)
    }
    try {
      // Ensure the exported plan carries the full legacy top-level shape so
      // the file opens in the legacy editor too (round-trip compatible).
      const exportPlan = deepMerge(LEGACY_SKELETON, plan)
      const b64 = await encryptPlan(exportPlan, password)
      const blob = new Blob([b64], { type: "application/octet-stream" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = suggestedFilename(plan)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      this._notify("Saved encrypted .sdp", "ok")
    } catch (e) {
      this._notify(`Encryption failed: ${e.message}`, "err")
    }
  }

  open(event) {
    event?.preventDefault()
    // Click the hidden <input type=file> sibling.
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  async fileChosen(event) {
    const file = event.currentTarget.files && event.currentTarget.files[0]
    event.currentTarget.value = ""  // allow re-opening the same file later
    if (!file) return
    const text = await file.text()
    const res = await this._promptPassword({
      title:   "Decrypt & Load",
      hint:    `Enter the password for ${file.name}.`,
      submit:  "Open",
      remember: true
    })
    if (!res || !res.password) return
    const password = res.password
    try {
      const plan = await decryptPlan(text.trim(), password)
      // Cache the successful open password (only if they asked us to) so
      // re-saving doesn't prompt again. Wrong-password failures don't
      // reach here so we only remember valid passwords.
      this._rememberPassword(res.remember ? password : null)
      // Confirm before overwriting the in-progress plan.
      const current = this._load()
      const hasContent = current && Object.keys(current).some(k => k !== "meta" && current[k])
      if (hasContent && !window.confirm("Opening a .sdp file will replace the plan in this browser. Continue?")) {
        return
      }
      replacePlan(plan)
      document.dispatchEvent(new CustomEvent("plan:change", {
        bubbles: true, detail: { path: "*", plan }
      }))
      this._notify(`Opened ${file.name}`, "ok")
      // Reload so every section's controller re-hydrates from the new plan.
      setTimeout(() => location.reload(), 250)
    } catch (e) {
      const msg = /OperationError|decrypt/i.test(e.message)
        ? "Wrong password or corrupted file."
        : `Open failed: ${e.message}`
      this._notify(msg, "err")
    }
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }

  // Custom password modal — replaces window.prompt() so the value is
  // masked by default and offers a show/hide toggle. Mirrors the legacy
  // editor's Encrypt & Save / Decrypt & Load dialog: an optional Confirm
  // Password field (opts.confirm) and a "Remember for this session"
  // checkbox (opts.remember). Resolves to { password, remember } or null
  // on cancel / Esc. Inline styles keep this self-contained (no CSS edits).
  _promptPassword({ title, hint, submit, confirm = false, remember = true }) {
    return new Promise(resolve => {
      // Default "Remember for this session" to CHECKED so the password is
      // cached after the FIRST save and re-saves don't re-prompt (the whole
      // point of the checkbox). The prompt only appears when there's no
      // cached password yet, so keying the default off _lastPassword meant
      // it was always unchecked — re-prompting on every single save.
      const rememberDefault = true
      const pwStyle = "width:100%;padding:10px 38px 10px 12px;font:14px/1.4 inherit;border:1px solid #cbd5e1;border-radius:8px;outline:none;box-sizing:border-box;color:#0f172a;background:#fff;"
      const overlay = document.createElement("div")
      overlay.setAttribute("role", "dialog")
      overlay.setAttribute("aria-modal", "true")
      overlay.style.cssText = "position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.55);backdrop-filter:blur(2px);font:14px/1.4 'Inter',ui-sans-serif,system-ui,sans-serif;"
      overlay.innerHTML = `
        <div style="width:min(92vw,420px);background:#fff;border-radius:14px;box-shadow:0 24px 48px rgba(0,0,0,.32),0 8px 16px rgba(0,0,0,.18);overflow:hidden;">
          <div style="padding:18px 20px 6px;">
            <h3 style="margin:0;font-size:16px;font-weight:700;color:#0f172a;">${title}</h3>
            <p style="margin:6px 0 14px;font-size:12.5px;color:#64748b;">${hint}</p>
          </div>
          <div style="padding:0 20px 18px;">
            <label style="display:block;font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px;">Password</label>
            <div style="position:relative;">
              <input type="password" autocomplete="new-password" spellcheck="false"
                     data-1p-ignore="true" data-lpignore="true" data-bwignore="true"
                     data-form-type="other"
                     name="pcpm_sdp_pw" id="pcpm_sdp_pw" data-role="pw"
                     style="${pwStyle}" />
              <button type="button" data-role="toggle"
                      title="Show password"
                      aria-label="Show password"
                      style="position:absolute;right:6px;top:50%;transform:translateY(-50%);width:28px;height:28px;display:inline-flex;align-items:center;justify-content:center;background:transparent;border:0;color:#64748b;cursor:pointer;border-radius:6px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              </button>
            </div>
            ${confirm ? `
            <label style="display:block;font-size:11px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.04em;margin:12px 0 6px;">Confirm Password</label>
            <input type="password" autocomplete="new-password" spellcheck="false"
                   data-1p-ignore="true" data-lpignore="true" data-bwignore="true"
                   data-form-type="other" name="pcpm_sdp_pw2" data-role="confirm"
                   style="width:100%;padding:10px 12px;font:14px/1.4 inherit;border:1px solid #cbd5e1;border-radius:8px;outline:none;box-sizing:border-box;color:#0f172a;background:#fff;" />
            <p data-role="mismatch" style="display:none;margin:6px 0 0;font-size:12px;font-weight:600;color:#dc2626;">Passwords don't match.</p>` : ""}
            ${remember ? `
            <label style="display:flex;align-items:center;gap:8px;margin:14px 0 0;font-size:13px;color:#334155;cursor:pointer;user-select:none;">
              <input type="checkbox" data-role="remember" ${rememberDefault ? "checked" : ""} style="width:15px;height:15px;accent-color:#4f46e5;cursor:pointer;" />
              Remember on this device
            </label>` : ""}
          </div>
          <div style="padding:0 20px 18px;display:flex;justify-content:flex-end;gap:8px;">
            <button type="button" data-role="cancel"
                    style="padding:8px 14px;font:600 13px/1 inherit;background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;border-radius:8px;cursor:pointer;">Cancel</button>
            <button type="button" data-role="submit"
                    style="padding:8px 14px;font:600 13px/1 inherit;background:#4f46e5;color:#fff;border:1px solid #4f46e5;border-radius:8px;cursor:pointer;">${submit}</button>
          </div>
        </div>`
      document.body.appendChild(overlay)

      const input       = overlay.querySelector('[data-role="pw"]')
      const confirmIn   = overlay.querySelector('[data-role="confirm"]')
      const mismatch    = overlay.querySelector('[data-role="mismatch"]')
      const rememberBox = overlay.querySelector('[data-role="remember"]')
      // Some password managers swap the input's type on mount to inject
      // their own widget. Force it back to "password" after one frame
      // so the field is masked by default no matter what.
      requestAnimationFrame(() => { if (input.type !== "text") input.type = "password" })
      const toggle   = overlay.querySelector('[data-role="toggle"]')
      const cancelBn = overlay.querySelector('[data-role="cancel"]')
      const submitBn = overlay.querySelector('[data-role="submit"]')

      const eyeOn  = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>'
      const eyeOff = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>'

      const cleanup = () => {
        document.removeEventListener("keydown", onKey)
        overlay.remove()
      }
      const finish = (val) => { cleanup(); resolve(val) }
      const trySubmit = () => {
        const pw = input.value || ""
        if (!pw) { input.focus(); return }
        if (confirm && confirmIn && confirmIn.value !== pw) {
          if (mismatch) mismatch.style.display = "block"
          confirmIn.focus()
          return
        }
        finish({ password: pw, remember: rememberBox ? !!rememberBox.checked : false })
      }
      const onKey = (e) => {
        if (e.key === "Escape") { e.preventDefault(); finish(null) }
        else if (e.key === "Enter") {
          const t = document.activeElement
          if (t === input || t === confirmIn) { e.preventDefault(); trySubmit() }
        }
      }
      if (confirmIn && mismatch) confirmIn.addEventListener("input", () => { mismatch.style.display = "none" })

      toggle.addEventListener("click", () => {
        const showing = input.type === "text"
        input.type = showing ? "password" : "text"
        toggle.innerHTML = showing ? eyeOn : eyeOff
        toggle.title = showing ? "Show password" : "Hide password"
        toggle.setAttribute("aria-label", toggle.title)
        input.focus()
      })
      cancelBn.addEventListener("click", () => finish(null))
      submitBn.addEventListener("click", trySubmit)
      overlay.addEventListener("click", (e) => { if (e.target === overlay) finish(null) })
      document.addEventListener("keydown", onKey)

      // Focus the input on next tick so the open animation doesn't steal it.
      requestAnimationFrame(() => input.focus())
    })
  }

  _notify(msg, kind = "ok") {
    let bar = document.getElementById("en-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "en-toast"
      bar.style.cssText = "position:fixed;top:12px;right:12px;padding:10px 14px;border-radius:8px;font:600 13px/1.2 ui-sans-serif,system-ui;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.18);pointer-events:none;"
      document.body.appendChild(bar)
    }
    bar.style.background = kind === "err" ? "#fee2e2" : kind === "warn" ? "#fef3c7" : "#dcfce7"
    bar.style.color      = kind === "err" ? "#991b1b" : kind === "warn" ? "#92400e" : "#166534"
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => { bar.style.opacity = "0" }, 3500)
  }
}
